import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { DataPerformanceSummaryType, EmblemType, TeamSectionItemType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import BoardPopupResolveComponent from 'webapp/components/board/popup-resolve';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type {
	TrackingOriginForDataboardDelete,
	TrackingOriginForDataboardDuplicate,
	TrackingOriginForFavoriteBoardAdded,
} from 'webapp/models-custom/board';
import type Board from 'webapp/models-custom/board';
import type TeamSectionItem from 'webapp/models-custom/team-section-item';

import type {
	TrackingOriginForTemplateCreated,
	TrackingOriginType,
	TrackingOriginTypeForAddLoop,
	TrackingOriginTypeForAddReport,
} from 'webapp/services/model-manager/boards';
import type { DataPerformanceSummaryObject } from 'webapp/services/model-manager/data-performance-summaries';
import type { Action as TeamAction } from 'webapp/services/team';

interface Args {
	model: Board;
	onOpenPreview: (model: Board, origin?: string) => void;
	hideSort?: boolean;
	isVisibleOnScreen?: boolean;
	onCopy?: (boardId: string) => void;
	batchSelect?: BatchSelect<Board>;
	teamSectionItem?: TeamSectionItem;
	removeFromSection?: (item: TeamSectionItem) => void;
	isRestricted?: boolean;
	checkVisibility?: boolean;
	onPermanentDelete?: () => void;
	onRestore?: () => void;
}

export type Action =
	| 'openPreview'
	| 'openWallShareSettings'
	| 'openFullscreenView'
	| 'openAccountTemplateEditPopup'
	| 'openPublicTemplateEditPopup'
	| 'copyBoard'
	| 'addToReport'
	| 'addToLoop'
	| 'addToTeam'
	| 'transferBoard'
	| 'transferBoardToAgency'
	| 'toggleMobileVisibility'
	| 'verify'
	| 'deleteBoard'
	| TeamAction
	| 'deletePermanently'
	| 'restore';

export type Option = PopupListItem<Action>;

export default class BoardsIndexItemBaseComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('connect') declare connectService: Service.Connect;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('team') declare teamService: Service.Team;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@tracked isVisibleOnScreen = false;
	@tracked isEditingName = false;
	@tracked editedName = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.isVisibleOnScreen) {
			this.isVisibleOnScreen = true; // used on boards home page to always show grid item
		}
	}

	get showAiChatButton(): boolean {
		return this.aiChatService.isEligibleForAiChat && !this.args.model.deleted;
	}

	get isActiveUserViewer(): boolean {
		return !!this.currentUserService.model?.isViewer;
	}

	get isDemoAccount(): boolean {
		return this.currentUserService.isDemo;
	}

	get canEditBoardName(): boolean {
		const isDemoAccount = this.isDemoAccount,
			canCurrentUserEdit = this.args.model.canCurrentUserEdit,
			canEdit = canCurrentUserEdit && !isDemoAccount,
			teamSectionItem = this.args.teamSectionItem;

		if (teamSectionItem) {
			return canEdit && teamSectionItem.isCurrentUserMember;
		}

		return canEdit;
	}

	get trackOrigin(): TrackingOriginType {
		return this.navigationService.activatedRoute.startsWith('boards') ? 'databoards' : 'home';
	}

	get emblemTypeName() {
		const model = this.args.model;

		if (model.model.newBadge) {
			return EmblemType.NEW;
		} else if (model.isPublicTemplate) {
			return EmblemType.PUBLIC_TEMPLATE;
		} else if (this.isDeleted) {
			return EmblemType.RECENTLY_DELETED;
		}

		return;
	}

	get isError(): boolean {
		return this.args.model.statusClass === 'error';
	}

	get dataPerformanceSummaryObject(): DataPerformanceSummaryObject {
		return {
			objectType: DataPerformanceSummaryType.BOARD,
			objectId: this.args.model.id || '',
		};
	}

	get showBoardSources(): boolean {
		return !(this.args.model.isAnythingToSolve && this.args.model.isRandom);
	}

	get greenbuttonText(): string {
		return this.args.model.isOnlyConnect ? 'Connect' : 'Reconnect';
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return !!batchSelect?.isItemSelected(model);
	}

	get isDeleted(): boolean {
		return !!this.args.model.deleted;
	}

	get showDragHandle(): boolean {
		const isDragHandleShown = !this.isDemoAccount && !this.isDeleted,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && isDragHandleShown
			: isDragHandleShown;
	}

	// ------ Teams Section Item ------

	get isTeamSectionItem(): boolean {
		return !!this.args.teamSectionItem && !!this.args.removeFromSection;
	}

	get teamSectionItemOptions(): Option[] {
		return this.teamService.getTeamSectionItemOptions(this.isTeamSectionItem);
	}

	get canCurrentUserEdit() {
		const canCurrentUserEdit = this.args.model.canCurrentUserEdit,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && canCurrentUserEdit
			: canCurrentUserEdit;
	}

	get canCurrentUserEditWithoutDeleted() {
		const canCurrentUserEditWithoutDeleted = this.args.model.canCurrentUserEditWithoutDeleted,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem
			? teamSectionItem.isCurrentUserMember && canCurrentUserEditWithoutDeleted
			: canCurrentUserEditWithoutDeleted;
	}

	get showActions() {
		const teamSectionItem = this.args.teamSectionItem;
		return teamSectionItem ? teamSectionItem.isCurrentUserMember : true;
	}

	@action
	doOptionsAction(actionName: string): void {
		if (actionName !== 'openPreview' && this.currentUserService.handleDemo()) {
			return;
		}

		this[actionName as Action]();
	}

	@action
	openPreview(): void {
		this.args.onOpenPreview(this.args.model, this.trackOrigin);
	}

	@action
	editBoardName(): void {
		if (this.args.model.canCurrentUserEdit) {
			this.editedName = this.args.model.name;
			this.isEditingName = true;
		}
	}

	@action
	checkBoardName(): void {
		const editedName = this.editedName.trim();

		if (editedName && editedName !== this.args.model.name) {
			this.args.model.name = editedName;
			this.args.model
				.save()
				.then((board) => {
					this.analyticsService.event(TrackingEvent.DATABOARD_RENAME, {
						databoard_id: board.id,
						origin: this.trackOrigin,
					});
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
		this.isEditingName = false;
	}

	@action
	toggleFavorite(): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.args.model.toggleFavorite(this.trackOrigin as TrackingOriginForFavoriteBoardAdded);
	}

	@action
	resolveSources() {
		this.mmBoardsService.resolveSources(this.args.model);
	}

	@action
	openPublicTemplateEditPopup() {
		this.mmBoardsService.openPublicTemplateEditPopup(this.args.model);
	}

	@action
	openAccountTemplateEditPopup() {
		this.mmBoardsService.openAccountTemplateEditPopup(
			this.args.model,
			this.trackOrigin as TrackingOriginForTemplateCreated,
		);
	}

	@action
	openMigrationTool() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.mmBoardsService.openMigrationTool(this.args.model);
	}

	@action
	resolveBoardConnections() {
		if (this.args.model.sourcesResolvable.length) {
			this.popupService
				.createModal(BoardPopupResolveComponent, {
					popupClass: 'popup-type-resolve-board title-bigger',
				})
				.open({
					model: this.args.model,
				});
		} else {
			this.navigationService.transitionToRoute('datamanager.connected');
		}
	}

	@action
	openFullscreenView() {
		this.args.model.openFullscreenView();
	}

	@action
	editBoard() {
		this.args.model.editBoard();
	}

	@action
	openWallShareSettings() {
		this.args.model.openWallShareSettings(this.trackOrigin);
	}

	@action
	openChangeOwnerPopup() {
		this.args.model.openChangeOwnerPopup();
	}

	@action
	copyBoard() {
		this.args.model.copyBoard(
			this.trackOrigin as TrackingOriginForDataboardDuplicate,
			false,
			this.args.onCopy,
		);
	}

	@action
	addToReport() {
		this.mmBoardsService.addToReport(
			this.args.model,
			this.trackOrigin as TrackingOriginTypeForAddReport,
		);
	}

	@action
	addToLoop() {
		this.mmBoardsService
			.addToLoop(this.args.model, this.trackOrigin as TrackingOriginTypeForAddLoop)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	@action
	addToTeam() {
		const itemId = this.args.model.id;

		if (itemId) {
			this.mmTeamSectionsService.openAddPopup({
				objectId: itemId,
				objectType: TeamSectionItemType.DATABOARD,
			});
		}
	}

	@action
	transferBoard() {
		this.mmBoardsService
			.openTransferPopup(this.args.model)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	@action
	transferBoardToAgency() {
		this.mmBoardsService
			.openTransferPopup(this.args.model, false, true)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	@action
	openActivityLog() {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.args.model,
		});
	}

	@action
	deleteBoard() {
		this.args.batchSelect?.unselectAll();
		this.args.model.deleteObject(this.trackOrigin as TrackingOriginForDataboardDelete);
	}

	@action
	deletePermanently() {
		this.args.batchSelect?.unselectAll();
		this.args.model.deleteObject(
			this.trackOrigin as TrackingOriginForDataboardDelete,
			() => {
				this.args.onPermanentDelete?.();
				return 0;
			},
			true,
		);
	}

	@action
	restore() {
		this.args.batchSelect?.unselectAll();

		const restorePromise = this.mmBoardsService.restore(this.args.model);
		if (restorePromise) {
			void restorePromise.then(() => {
				this.args.onRestore?.();
			});
		}
	}

	@action
	toggleMobileVisibility() {
		const layer = this.infolayerService.loading('Saving...');

		this.args.model.hidden = !this.args.model.hidden;
		this.args.model
			.save()
			.then((board) => {
				layer.ok(
					'Dashboard <strong>' +
						board.get('name') +
						'</strong> ' +
						(board.hidden ? 'hidden' : 'visible') +
						' on mobile!',
				);
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	createNotification() {
		this.mmBoardsService.createAndOpenNewNotificationForModel(this.args.model, 'databoards');
	}

	@action
	selectItem(selected: boolean): void {
		const { model, batchSelect } = this.args;
		batchSelect?.changeSelection(model, selected);
	}

	@action
	removeFromSection() {
		const teamSectionItem = this.args.teamSectionItem;

		if (teamSectionItem) {
			this.args.removeFromSection?.(teamSectionItem);
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	openGenie(): void {
		this.aiChatService.openGenieForBoard(this.args.model);
	}

	@action
	verify(): void {
		void this.mmBoardsService.toggleVerification(this.args.model);
	}
}
